import React from 'react';
import { Info, X } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import DemoContent from '../components/DemoContent';
import { VideoShowcase } from '../../src/components/communities/montana/VideoShowcase';

const DemoPage = () => {
  // Tab configurations with stable IDs and display labels
  const tabConfigs = React.useMemo(
    () => ({
      community: [
        { id: 'voting', label: 'Voting' },
        { id: 'disputes', label: 'Disputes' },
        { id: 'financial-reports', label: 'Financial Reports' },
      ],
      resident: [
        { id: 'voting', label: 'Voting' },
        { id: 'disputes', label: 'Disputes' },
        { id: 'proposals', label: 'Proposals' },
        { id: 'pay-fees', label: 'Pay Fees' },
        { id: 'group-buys', label: 'Group Buys' },
        { id: 'facility-booking', label: 'Facility Booking' },
      ],
      vendor: [
        { id: 'group-buys', label: 'Group Buys' },
        { id: 'payment-processing', label: 'Payment Processing' },
      ],
    }),
    [],
  );

  // State to manage active tabs for each user flow
  const [activeCommunityTab, setActiveCommunityTab] = React.useState('voting');
  const [activeResidentTab, setActiveResidentTab] = React.useState('voting');
  const [activeVendorTab, setActiveVendorTab] = React.useState('group-buys');
  const [activeRole, setActiveRole] = React.useState<'community' | 'resident' | 'vendor'>(
    'resident',
  );
  const [activeSubTab, setActiveSubTab] = React.useState('overview');
  const [searchParams, setSearchParams] = useSearchParams();

  // Allowed values for URL sync
  const allowedRoles = React.useMemo(() => ['resident', 'community', 'vendor'] as const, []);
  const communityTabIds = React.useMemo(() => tabConfigs.community.map((t) => t.id), [tabConfigs]);
  const residentTabIds = React.useMemo(() => tabConfigs.resident.map((t) => t.id), [tabConfigs]);
  const vendorTabIds = React.useMemo(() => tabConfigs.vendor.map((t) => t.id), [tabConfigs]);

  // Initialize state from URL on mount and when URL changes (e.g., pasted deep link)
  React.useEffect(() => {
    const roleParam = searchParams.get('role');
    if (roleParam && (allowedRoles as readonly string[]).includes(roleParam)) {
      setActiveRole(roleParam as typeof activeRole);
    }

    const rTab = searchParams.get('residentTab');
    if (rTab && residentTabIds.includes(rTab)) {
      setActiveResidentTab(rTab);
    }

    const cTab = searchParams.get('communityTab');
    if (cTab && communityTabIds.includes(cTab)) {
      setActiveCommunityTab(cTab);
    }

    const vTab = searchParams.get('vendorTab');
    if (vTab && vendorTabIds.includes(vTab)) {
      setActiveVendorTab(vTab);
    }

    const subTab = searchParams.get('subTab');
    if (subTab && ['overview', 'process', 'features', 'benefits'].includes(subTab)) {
      setActiveSubTab(subTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Small helper to update a single param without losing others
  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  // Gentle helper note on first visit
  const [showRoleHint, setShowRoleHint] = React.useState(false);
  React.useEffect(() => {
    const dismissed =
      typeof window !== 'undefined' && window.localStorage.getItem('demoRoleHintDismissed') === '1';
    if (!dismissed) {
      const t = setTimeout(() => setShowRoleHint(true), 600);
      return () => clearTimeout(t);
    }
  }, []);
  const dismissHint = () => {
    setShowRoleHint(false);
    try {
      window.localStorage.setItem('demoRoleHintDismissed', '1');
    } catch (e) {
      // ignore storage errors (private mode, quotas, etc.)
    }
  };

  // Demo content data with updated keys
  const demoContent = {
    'community-voting': {
      title: 'Community Board Voting',
      videoTitle: 'How Board Members Make Decisions',
      content: {
        overview:
          "Board members use fact-based voting to make important community decisions. Every vote requires specific reasons that relate to the community's best interests.\n\nThis system ensures decisions are made on facts, not personal opinions or emotions.",
        process:
          'When voting on community matters, board members select their position and provide specific reasoning.\n\nThe system checks that the reasoning is factual and relevant to the issue at hand.\n\nOnly votes with proper reasoning count toward the final decision.',
        features:
          'Transparent decision-making with all reasoning visible to the community.\n\nBuilt-in validation ensures votes are based on facts, not emotions.\n\nComplete audit trail of all board decisions and the reasoning behind them.',
        benefits:
          'Better community decisions based on facts and evidence.\n\nIncreased trust from residents who can see exactly why decisions were made.\n\nReduced conflicts and disputes over board actions.',
      },
    },
    'community-disputes': {
      title: 'Community Dispute Resolution',
      videoTitle: 'How the Board Handles Community Disputes',
      content: {
        overview:
          "When community issues arise, the board has tools to manage and resolve disputes fairly and efficiently.\n\nThis system helps maintain peace and order while protecting everyone's rights.",
        process:
          'Disputes are filed through the system with clear documentation.\n\nBoard members review evidence and make decisions based on community rules.\n\nAll parties receive updates and can track the resolution process.',
        features:
          'Structured dispute filing with required documentation.\n\nClear timelines for resolution to ensure prompt action.\n\nFair hearing process with opportunity for all parties to present their case.',
        benefits:
          'Faster resolution of community conflicts.\n\nFair process that treats all residents equally.\n\nReduced legal costs and formal complaints.',
      },
    },
    'community-financial-reports': {
      title: 'Community Financial Reports',
      videoTitle: 'Track Community Finances and Budgets',
      content: {
        overview:
          'Board members have access to comprehensive financial reports to monitor community finances and make informed budget decisions.\n\nReal-time reporting helps identify issues early and maintain financial health.',
        process:
          'Access financial dashboards from your board portal.\n\nView income, expenses, and budget vs. actual spending.\n\nGenerate reports for board meetings and resident communications.\n\nTrack special assessments and reserve fund status.',
        features:
          'Real-time financial dashboards with key metrics.\n\nBudget tracking with alerts for overspending.\n\nResident payment status and collections reporting.\n\nReserve fund management and planning tools.',
        benefits:
          'Better financial oversight and accountability.\n\nEarly warning system for budget issues.\n\nTransparent reporting for resident communications.\n\nImproved financial planning and decision-making.',
      },
    },
    'resident-voting': {
      title: 'Resident Voting',
      videoTitle: 'How Residents Participate in Community Decisions',
      content: {
        overview:
          'As a resident, you have a voice in community decisions that affect your daily life.\n\nThe voting system is designed to be simple and ensure every opinion is heard fairly.',
        process:
          "You'll receive notifications when there's something to vote on.\n\nClick the link, read the details, and select your choice.\n\nAdd a brief explanation of why you chose that option.\n\nYour vote is counted and you'll see the results.",
        features:
          "Easy-to-use voting from your phone or computer.\n\nClear explanations of what you're voting on.\n\nYour vote stays private but your reasoning helps the community understand different viewpoints.",
        benefits:
          "Your voice matters in community decisions.\n\nBetter outcomes because decisions consider all residents' needs.\n\nTransparent process so you know how decisions are made.",
      },
    },
    'resident-disputes': {
      title: 'Resident Dispute Filing',
      videoTitle: 'How to Report Issues and Get Help',
      content: {
        overview:
          "When you have a problem with a neighbor, maintenance, or community rule, there's a clear process to get help.\n\nThe system ensures your concerns are heard and addressed fairly.",
        process:
          "Use the simple online form to describe your issue.\n\nUpload any photos or documents that help explain the problem.\n\nYou'll get a case number to track progress.\n\nReceive updates as your case moves through the resolution process.",
        features:
          'Easy online filing - no paperwork or office visits required.\n\nPhoto upload to document issues clearly.\n\nAutomatic updates sent to your phone or email.\n\nClear timeline so you know what to expect.',
        benefits:
          'Quick and easy way to get help with problems.\n\nFair process that protects your rights as a resident.\n\nFaster resolution than traditional complaint processes.',
      },
    },
    'resident-proposals': {
      title: 'Community Proposals',
      videoTitle: 'How Residents Can Suggest Improvements',
      content: {
        overview:
          'Have an idea to make your community better? You can submit proposals for new amenities, rule changes, or improvements.\n\nOther residents and the board will review and vote on your suggestions.',
        process:
          'Submit your idea through the simple online form.\n\nOther residents can comment and show support.\n\nThe board reviews proposals with community support.\n\nVoting takes place and you see the results.',
        features:
          'Easy proposal submission with templates to guide you.\n\nCommunity feedback system to build support for good ideas.\n\nTransparent review process with clear criteria.\n\nRegular voting cycles so proposals move forward quickly.',
        benefits:
          "Your ideas can actually improve the community.\n\nDemocratic process ensures popular ideas get implemented.\n\nCommunity becomes more responsive to residents' needs.",
      },
    },
    'resident-pay-fees': {
      title: 'Pay Community Fees',
      videoTitle: 'Simple and Secure Payment Options',
      content: {
        overview:
          'Pay your HOA fees, special assessments, and facility bookings quickly and securely online.\n\nMultiple payment options make it convenient for every resident.',
        process:
          'Log into your resident portal to see what you owe.\n\nChoose your preferred payment method.\n\nComplete the secure payment process.\n\nReceive confirmation and receipt immediately.',
        features:
          'Multiple payment options: bank transfer, credit card, or digital wallet.\n\nAutomatic payment setup available for regular fees.\n\nInstant receipts and payment confirmation.\n\nSecure encryption protects your financial information.',
        benefits:
          'No more checks or trips to the office.\n\nPay anytime, anywhere from your phone or computer.\n\nNever miss a payment with automatic reminders.\n\nLower fees through efficient electronic processing.',
      },
    },
    'resident-group-buys': {
      title: 'Group Buying',
      videoTitle: 'Save Money Through Community Group Purchases',
      content: {
        overview:
          'Join with your neighbors to buy services and products at discounted group rates.\n\nFrom landscaping to home security, group buying saves everyone money.',
        process:
          'Browse available group buying opportunities.\n\nJoin groups that interest you with one click.\n\nPay your share when the group reaches the minimum size.\n\nReceive your service or product at the group discount price.',
        features:
          'Wide variety of services: landscaping, pest control, home security, and more.\n\nAutomatic group formation when enough neighbors join.\n\nSecure group payment processing.\n\nQuality vendors pre-screened by the community.',
        benefits:
          'Significant savings compared to individual purchases.\n\nConvenience of coordinated service delivery.\n\nTried and tested vendors recommended by neighbors.\n\nStronger community connections through shared activities.',
      },
    },
    'resident-facility-booking': {
      title: 'Facility Booking',
      videoTitle: 'Reserve Community Amenities',
      content: {
        overview:
          'Book community facilities like the clubhouse, tennis court, or pool area for personal events or activities.\n\nSimple online booking with instant confirmation and secure payment.',
        process:
          'Browse available facilities and see their calendars.\n\nSelect your preferred date and time.\n\nComplete the booking form with event details.\n\nPay any required fees and receive instant confirmation.',
        features:
          'Real-time availability calendar for all facilities.\n\nOnline payment for facility fees and deposits.\n\nAutomatic confirmation and reminder emails.\n\nEasy cancellation and refund process.',
        benefits:
          'Book facilities anytime without office visits.\n\nNo double-bookings with real-time availability.\n\nSecure payment processing for fees and deposits.\n\nClear facility rules and guidelines upfront.',
      },
    },
    'vendor-group-buys': {
      title: 'Vendor Group Sales',
      videoTitle: 'Grow Your Business Through Community Partnerships',
      content: {
        overview:
          'Reach entire communities with group purchasing programs that benefit both you and residents.\n\nStreamlined process makes it easy to offer group discounts and manage community contracts.',
        process:
          'Submit your service proposal with group pricing.\n\nCommunity members sign up for your group offer.\n\nReceive notification when minimum group size is reached.\n\nProvide service to all group members according to your agreement.',
        features:
          'Access to entire communities through one partnership.\n\nAutomated group formation and payment collection.\n\nMarketing support through community channels.\n\nStreamlined contracting and billing process.',
        benefits:
          'Larger volume sales with guaranteed minimum orders.\n\nReduced marketing costs through community partnerships.\n\nSimplified billing with single community payments.\n\nBuilding long-term relationships with entire communities.',
      },
    },
    'vendor-payment-processing': {
      title: 'Vendor Payment Processing',
      videoTitle: 'Get Paid Quickly and Securely',
      content: {
        overview:
          'Streamlined payment processing for vendor services makes it easy to get paid by communities and individual residents.\n\nMultiple payment options and automatic processing reduce delays.',
        process:
          'Submit invoices through the vendor portal.\n\nCommunity approves payment according to contract terms.\n\nAutomatic payment processing on scheduled dates.\n\nReceive payment confirmation and detailed records.',
        features:
          'Electronic invoice submission and tracking.\n\nAutomatic payment scheduling based on contracts.\n\nMultiple payment methods: ACH, wire transfer, check.\n\nDetailed payment history and tax reporting.',
        benefits:
          'Faster payment processing with fewer delays.\n\nReduced administrative overhead for billing.\n\nClear payment terms and automatic processing.\n\nBetter cash flow with predictable payment schedules.',
      },
    },
  };

  const renderContent = (userFlow: string, tabId: string) => {
    const key = `${userFlow}-${tabId}`;
    const content = demoContent[key as keyof typeof demoContent];

    if (!content) {
      return <div className="p-8 text-center text-gray-500">Demo content coming soon...</div>;
    }

    return (
      <DemoContent
        title={content.title}
        videoTitle={content.videoTitle}
        content={content.content}
        activeSubTab={activeSubTab}
        onSubTabChange={(subTab) => {
          setActiveSubTab(subTab);
          updateParam('subTab', subTab);
        }}
      />
    );
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-slate-50 to-blue-50/30">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-20 bg-linear-to-r from-blue-600/10 to-indigo-600/10">
        <div className="absolute inset-0 bg-linear-to-r from-blue-600/5 to-indigo-600/5" />
        <div className="relative container max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 mb-6 bg-blue-100 text-blue-800 border border-blue-200 rounded-full text-sm font-medium">
            🎯 Interactive Demo Walkthrough
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 text-slate-900 leading-tight">
            Experience Our Platform
            <span className="block text-blue-600 mt-2">Live Demo Walkthroughs</span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-600 mb-12 max-w-4xl mx-auto leading-relaxed">
            Explore features tailored for{' '}
            <strong className="text-slate-800">community managers</strong>,
            <strong className="text-slate-800"> residents</strong>, and{' '}
            <strong className="text-slate-800">vendors</strong>
          </p>

          {/* Top-level Role Switcher */}
          <div className="flex flex-col items-center">
            <div
              role="tablist"
              aria-label="Select audience role"
              className="inline-flex rounded-lg bg-white/90 backdrop-blur-sm shadow-xs ring-1 ring-slate-200 overflow-hidden"
              onKeyDown={(e) => {
                const roles = allowedRoles;
                const currentIndex = roles.indexOf(activeRole);
                let newIndex = currentIndex;

                if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
                  newIndex = currentIndex > 0 ? currentIndex - 1 : roles.length - 1;
                  e.preventDefault();
                } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
                  newIndex = currentIndex < roles.length - 1 ? currentIndex + 1 : 0;
                  e.preventDefault();
                }

                if (newIndex !== currentIndex) {
                  const newRole = roles[newIndex];
                  setActiveRole(newRole);
                  updateParam('role', newRole);
                  // Focus the new tab
                  const tabButton = e.currentTarget.querySelector(
                    `[data-role="${newRole}"]`,
                  ) as HTMLButtonElement;
                  tabButton?.focus();
                }
              }}
            >
              <button
                role="tab"
                aria-selected={activeRole === 'resident'}
                aria-controls="resident-panel"
                data-role="resident"
                tabIndex={activeRole === 'resident' ? 0 : -1}
                onClick={() => {
                  setActiveRole('resident');
                  updateParam('role', 'resident');
                  updateParam('residentTab', activeResidentTab);
                }}
                className={`px-4 md:px-6 py-2 md:py-3 text-sm md:text-base font-medium transition-all focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 ${
                  activeRole === 'resident'
                    ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-500/50'
                    : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                Resident
              </button>
              <button
                role="tab"
                aria-selected={activeRole === 'community'}
                aria-controls="community-panel"
                data-role="community"
                tabIndex={activeRole === 'community' ? 0 : -1}
                onClick={() => {
                  setActiveRole('community');
                  updateParam('role', 'community');
                  updateParam('communityTab', activeCommunityTab);
                }}
                className={`px-4 md:px-6 py-2 md:py-3 text-sm md:text-base font-medium transition-all focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 border-l border-slate-200/80 ${
                  activeRole === 'community'
                    ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-500/50'
                    : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                Board/Admin
              </button>
              <button
                role="tab"
                aria-selected={activeRole === 'vendor'}
                aria-controls="vendor-panel"
                data-role="vendor"
                tabIndex={activeRole === 'vendor' ? 0 : -1}
                onClick={() => {
                  setActiveRole('vendor');
                  updateParam('role', 'vendor');
                  updateParam('vendorTab', activeVendorTab);
                }}
                className={`px-4 md:px-6 py-2 md:py-3 text-sm md:text-base font-medium transition-all focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500 border-l border-slate-200/80 ${
                  activeRole === 'vendor'
                    ? 'bg-blue-600 text-white shadow-md ring-2 ring-blue-500/50'
                    : 'text-slate-700 hover:bg-slate-50 hover:text-slate-900'
                }`}
              >
                Vendor
              </button>
            </div>

            {showRoleHint && (
              <div
                role="status"
                className="mt-4 inline-flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 text-blue-900 px-4 py-3 shadow-xs animate-fade-in"
              >
                <Info className="h-5 w-5 mt-0.5 text-blue-600" />
                <div className="text-left text-sm">
                  <p className="font-medium">Explore role-based demos</p>
                  <p className="text-blue-800/90">
                    Select a role above to see a tailored walkthrough for Residents, Board/Admins,
                    or Vendors.
                  </p>
                </div>
                <button
                  aria-label="Dismiss hint"
                  className="ml-2 rounded p-1 text-blue-700 hover:bg-blue-100 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500"
                  onClick={dismissHint}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Demo Content */}
      <section className="py-16 lg:py-20">
        <div className="container max-w-7xl mx-auto px-6 lg:px-8">
          {activeRole === 'community' && (
            <div
              className="mb-16 lg:mb-24"
              role="tabpanel"
              id="community-panel"
              aria-labelledby="community-tab"
            >
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                  Community Management
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Tools for board members and community administrators to manage governance,
                  finances, and operations
                </p>
              </div>
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
                <div className="border-b border-slate-200 bg-slate-50">
                  <nav
                    role="tablist"
                    aria-label="Community management features"
                    className="flex flex-wrap justify-center lg:justify-start px-6"
                  >
                    {tabConfigs.community.map((tab, index) => (
                      <button
                        key={tab.id}
                        role="tab"
                        aria-selected={activeCommunityTab === tab.id}
                        aria-controls={`community-${tab.id}-content`}
                        tabIndex={activeCommunityTab === tab.id ? 0 : -1}
                        onClick={() => {
                          setActiveCommunityTab(tab.id);
                          updateParam('communityTab', tab.id);
                        }}
                        className={`py-4 px-6 font-medium text-sm transition-all duration-200 border-b-2 ${
                          activeCommunityTab === tab.id
                            ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                            : 'border-transparent text-slate-600 hover:text-blue-600 hover:border-blue-300'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>
                <div
                  className="p-6 lg:p-8"
                  role="tabpanel"
                  id={`community-${activeCommunityTab}-content`}
                  aria-labelledby={`community-${activeCommunityTab}-tab`}
                >
                  {renderContent('community', activeCommunityTab)}

                  {/* Secondary CTA */}
                  <div className="mt-8 pt-6 border-t border-slate-200">
                    <div className="text-center">
                      <p className="text-slate-600 mb-4">
                        Ready to streamline your community management?
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500">
                          Schedule a Board Demo
                        </button>
                        <button className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500">
                          Talk to Sales
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeRole === 'resident' && (
            <div
              className="mb-16 lg:mb-24"
              role="tabpanel"
              id="resident-panel"
              aria-labelledby="resident-tab"
            >
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                  Resident Services
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Everything residents need to participate in community life, from voting to
                  payments
                </p>
              </div>
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
                <div className="border-b border-slate-200 bg-slate-50">
                  <nav
                    role="tablist"
                    aria-label="Resident services features"
                    className="flex flex-wrap justify-center lg:justify-start px-6 gap-x-2"
                  >
                    {tabConfigs.resident.map((tab, index) => (
                      <button
                        key={tab.id}
                        role="tab"
                        aria-selected={activeResidentTab === tab.id}
                        aria-controls={`resident-${tab.id}-content`}
                        tabIndex={activeResidentTab === tab.id ? 0 : -1}
                        onClick={() => {
                          setActiveResidentTab(tab.id);
                          updateParam('residentTab', tab.id);
                        }}
                        className={`py-4 px-4 lg:px-6 font-medium text-sm transition-all duration-200 border-b-2 whitespace-nowrap ${
                          activeResidentTab === tab.id
                            ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                            : 'border-transparent text-slate-600 hover:text-blue-600 hover:border-blue-300'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>
                <div
                  className="p-6 lg:p-8"
                  role="tabpanel"
                  id={`resident-${activeResidentTab}-content`}
                  aria-labelledby={`resident-${activeResidentTab}-tab`}
                >
                  {renderContent('resident', activeResidentTab)}

                  {/* Secondary CTA */}
                  <div className="mt-8 pt-6 border-t border-slate-200">
                    <div className="text-center">
                      <p className="text-slate-600 mb-4">
                        Experience the resident portal for yourself
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500">
                          Try Live Demo
                        </button>
                        <button className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500">
                          Learn More
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeRole === 'vendor' && (
            <div className="mb-16" role="tabpanel" id="vendor-panel" aria-labelledby="vendor-tab">
              <div className="text-center mb-12">
                <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
                  Vendor Portal
                </h2>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  Streamlined tools for service providers to work with communities and get paid
                  efficiently
                </p>
              </div>
              <div className="bg-white rounded-2xl shadow-xl border border-slate-200 overflow-hidden">
                <div className="border-b border-slate-200 bg-slate-50">
                  <nav
                    role="tablist"
                    aria-label="Vendor portal features"
                    className="flex flex-wrap justify-center lg:justify-start px-6"
                  >
                    {tabConfigs.vendor.map((tab, index) => (
                      <button
                        key={tab.id}
                        role="tab"
                        aria-selected={activeVendorTab === tab.id}
                        aria-controls={`vendor-${tab.id}-content`}
                        tabIndex={activeVendorTab === tab.id ? 0 : -1}
                        onClick={() => {
                          setActiveVendorTab(tab.id);
                          updateParam('vendorTab', tab.id);
                        }}
                        className={`py-4 px-6 font-medium text-sm transition-all duration-200 border-b-2 ${
                          activeVendorTab === tab.id
                            ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                            : 'border-transparent text-slate-600 hover:text-blue-600 hover:border-blue-300'
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>
                <div
                  className="p-6 lg:p-8"
                  role="tabpanel"
                  id={`vendor-${activeVendorTab}-content`}
                  aria-labelledby={`vendor-${activeVendorTab}-tab`}
                >
                  {renderContent('vendor', activeVendorTab)}

                  {/* Secondary CTA */}
                  <div className="mt-8 pt-6 border-t border-slate-200">
                    <div className="text-center">
                      <p className="text-slate-600 mb-4">
                        Join our vendor network and grow your business
                      </p>
                      <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <button className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500">
                          Apply as Vendor
                        </button>
                        <button className="px-6 py-3 border border-slate-300 text-slate-700 rounded-lg font-medium hover:bg-slate-50 transition-colors focus:outline-hidden focus-visible:ring-2 focus-visible:ring-blue-500">
                          Partnership Info
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Video Showcase Section */}
      <section className="py-16 bg-white">
        <VideoShowcase />
      </section>
    </div>
  );
};

export default DemoPage;
